import { MONTHS, normalizeTypedBirthDate } from './birthDateInputGuard';

/**
 * Plain string logic behind shared/DatePicker (IGFEMS-673). Values stay "YYYY-MM-DD" or
 * "YYYY-MM-DDTHH:mm" with no Date in between, so they never shift across time zones.
 */
export const DATE = 'date';
export const DATETIME = 'datetime';

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export { MONTHS };

const VALUE_RE = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}))?/;
const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;
const MIDNIGHT = '00:00';

const pad = (n) => String(n).padStart(2, '0');

export const makeDayKey = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;

export const makeValue = (dayKey, time, variant) => (variant === DATETIME ? `${dayKey}T${time || MIDNIGHT}` : dayKey);

export const todayKey = () => {
  const now = new Date();
  return makeDayKey(now.getFullYear(), now.getMonth(), now.getDate());
};

/** The stored value in this variant's shape, or '' when it is missing or not a date. */
export const normalizeValue = (value, variant) => {
  const match = VALUE_RE.exec(String(value ?? ''));
  if (!match) return '';
  return makeValue(match[1], match[2], variant);
};

/** "2026-10-07T10:30" -> "07-Oct-2026 10:30" */
export const formatValue = (value) => {
  const match = VALUE_RE.exec(value || '');
  if (!match) return '';
  const [year, month, day] = match[1].split('-');
  const date = `${day}-${MONTHS[Number(month) - 1]}-${year}`;
  return match[2] ? `${date} ${match[2]}` : date;
};

/** Typed text as a value, or '' unless it is a real date. A datetime typed without a time is midnight. */
export const parseTyped = (text, variant) => {
  const [datePart, timePart] = String(text || '').trim().split(/\s+/);
  const clean = normalizeTypedBirthDate(datePart);
  if (!clean) return '';
  const [day, month, year] = clean.split('-');
  if (Number(year) < 1000) return '';
  const dayKey = makeDayKey(year, MONTHS.indexOf(month), Number(day));
  if (variant !== DATETIME) return dayKey;
  if (timePart && !TIME_RE.test(timePart)) return '';
  return makeValue(dayKey, timePart?.padStart(5, '0'), variant);
};

/** Works for any precision: "2026", "2026-10", a day key or a datetime are compared at their shared length. */
export const isInRange = (key, min, max) => {
  const atOrAfter = (bound) => key.slice(0, bound.length) >= bound.slice(0, key.length);
  const atOrBefore = (bound) => key.slice(0, bound.length) <= bound.slice(0, key.length);
  return (!min || atOrAfter(min)) && (!max || atOrBefore(max));
};

/** Why typed text cannot be used, in words for the user, or '' when it can. */
export const typedProblem = (text, variant, min, max) => {
  const parsed = parseTyped(text, variant);
  if (!parsed) return `Enter a valid date, like ${variant === DATETIME ? '07-Oct-2026 14:30' : '07-Oct-2026'}`;
  if (isInRange(parsed, min, max)) return '';
  if (min && max) return `Choose a date from ${formatValue(min)} to ${formatValue(max)}`;
  return min ? `Choose a date on or after ${formatValue(min)}` : `Choose a date on or before ${formatValue(max)}`;
};

/** Six weeks starting on Sunday, so the calendar never changes height between months. */
export const buildMonthGrid = (year, month) => {
  const leading = new Date(year, month, 1).getDay();
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(year, month, i - leading + 1);
    return {
      key: makeDayKey(date.getFullYear(), date.getMonth(), date.getDate()),
      day: date.getDate(),
      outside: date.getMonth() !== month,
    };
  });
};
